import { useState, useRef, useEffect } from "react";
import { DISTRICT_HIERARCHY } from "./muscleConfig";

type DistrictDropdownProps = {
  selectedDistricts: string[];
  onSelectionChange: (districts: string[]) => void;
};

export const DistrictDropdown: React.FC<DistrictDropdownProps> = ({
  selectedDistricts,
  onSelectionChange,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Chiudi dropdown al click fuori
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggleDistrict = (district: string) => {
    if (selectedDistricts.includes(district)) {
      onSelectionChange(selectedDistricts.filter((d) => d !== district));
    } else {
      onSelectionChange([...selectedDistricts, district]);
    }
  };

  const toggleGroup = (group: typeof DISTRICT_HIERARCHY[0]) => {
    const allSelected = group.children.every((c) => selectedDistricts.includes(c));
    if (allSelected) {
      // Deseleziona tutti i figli del gruppo
      onSelectionChange(selectedDistricts.filter((d) => !group.children.includes(d)));
    } else {
      // Seleziona tutti i figli del gruppo
      const newSelection = new Set(selectedDistricts);
      group.children.forEach((c) => newSelection.add(c));
      onSelectionChange(Array.from(newSelection));
    }
  };

  // Label per il bottone
  const getButtonLabel = () => {
    if (selectedDistricts.length === 0) return "Seleziona muscolo...";
    if (selectedDistricts.length <= 2) return selectedDistricts.join(", ");
    return `${selectedDistricts.length} muscoli selezionati`;
  };

  return (
    <div ref={ref} className="relative inline-block w-full max-w-xs">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-sm text-gray-200 hover:bg-gray-600 transition-colors"
      >
        <span className="truncate">{getButtonLabel()}</span>
        <svg
          className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-gray-800 border border-gray-600 rounded-lg shadow-xl max-h-80 overflow-y-auto">
          {/* Reset */}
          <button
            onClick={() => onSelectionChange([])}
            className="w-full px-4 py-2 text-xs text-gray-400 hover:bg-gray-700 text-left border-b border-gray-700"
          >
            Reset selezione
          </button>

          {DISTRICT_HIERARCHY.map((group) => {
            const allSelected = group.children.every((c) => selectedDistricts.includes(c));
            const someSelected = group.children.some((c) => selectedDistricts.includes(c));
            const isSingle = group.children.length === 1;

            return (
              <div key={group.label}>
                {/* Header gruppo */}
                <button
                  onClick={() => isSingle ? toggleDistrict(group.children[0]) : toggleGroup(group)}
                  className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-700 transition-colors border-b border-gray-700/50"
                >
                  <span
                    className={`w-4 h-4 rounded flex-shrink-0 border flex items-center justify-center text-[10px] ${
                      allSelected
                        ? "bg-blue-500 border-blue-500 text-white"
                        : someSelected
                        ? "bg-blue-500/30 border-blue-500 text-blue-300"
                        : "border-gray-500"
                    }`}
                  >
                    {allSelected ? "✓" : someSelected ? "−" : ""}
                  </span>
                  <span className="text-sm font-semibold text-gray-200">{group.label}</span>
                </button>

                {/* Figli (solo se il gruppo ha più di un figlio) */}
                {!isSingle &&
                  group.children.map((child) => {
                    const isSelected = selectedDistricts.includes(child);
                    return (
                      <button
                        key={child}
                        onClick={() => toggleDistrict(child)}
                        className="w-full flex items-center gap-2 pl-8 pr-4 py-1.5 hover:bg-gray-700 transition-colors"
                      >
                        <span
                          className={`w-3.5 h-3.5 rounded-sm flex-shrink-0 border flex items-center justify-center text-[9px] ${
                            isSelected
                              ? "bg-blue-500 border-blue-500 text-white"
                              : "border-gray-500"
                          }`}
                        >
                          {isSelected ? "✓" : ""}
                        </span>
                        <span className={`text-xs ${isSelected ? "text-gray-200" : "text-gray-400"}`}>
                          {child}
                        </span>
                      </button>
                    );
                  })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
